import React from 'react';
import { 
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { 
  Language as LanguageIcon,
  MoreVert as MoreVertIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';

function KnowledgeBaseTable({ filteredKnowledgeBases, searchTerm, onDocumentClick, onMenuOpen }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'url':
        return <LanguageIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
      case 'text':
        return <TextFieldsIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
      default:
        return <LanguageIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
    }
  };

  const getDocumentContent = (item) => {
    switch (item.type) {
      case 'url':
        return item.knowledgeBaseUrl || 'No URL provided';
      case 'text':
        return null; // Don't show content for text types
      default:
        return item.knowledgeBaseUrl || 'No content provided';
    }
  };

  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      {/* Desktop Table View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Last updated</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredKnowledgeBases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
                    {searchTerm ? 'No documents match your search.' : 'No knowledge base documents found. Click "Add URL" to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredKnowledgeBases.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell 
                      onClick={() => onDocumentClick(item)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f3f4f6' }
                      }}
                    >
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {getDocumentIcon(item.type)}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name || item.knowledgeBaseId || 'Unnamed Document'}
                          </Typography>
                        </Box>
                        {getDocumentContent(item) && (
                          <Typography variant="caption" sx={{ color: '#6b7280', wordBreak: 'break-all' }}>
                            {getDocumentContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {formatDate(item.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => onMenuOpen(e, item)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {filteredKnowledgeBases.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
            <Typography variant="body1">
              {searchTerm ? 'No documents match your search.' : 'No knowledge base documents found. Click "Add URL" to get started.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {filteredKnowledgeBases.map((item) => (
              <Card 
                key={item._id} 
                sx={{ 
                  mb: 2, 
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 3,
                    backgroundColor: '#f9fafb'
                  }
                }}
                onClick={() => onDocumentClick(item)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getDocumentIcon(item.type)}
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {item.name || item.knowledgeBaseId || 'Unnamed Document'}
                      </Typography>
                    </Box>
                    {getDocumentContent(item) && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#6b7280', 
                          wordBreak: 'break-all',
                          fontSize: '12px',
                          lineHeight: 1.4
                        }}
                      >
                        {getDocumentContent(item)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuOpen(e, item);
                    }}
                    sx={{ ml: 1, flexShrink: 0 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '12px' }}>
                    {formatDate(item.updatedAt)}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default KnowledgeBaseTable;
